import { getAvailabilitySlotsForRange } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

export type UseSchedulerAppointmentSlotsProps = {
  providerId: string;
  daysAheadLimit: number;
  patientId?: string;
  excludeProviderIds?: string[];
  enabled?: boolean;
};

export const useSchedulerSlots = (
  { providerId, patientId, excludeProviderIds, daysAheadLimit, enabled = true }: UseSchedulerAppointmentSlotsProps,
  opts?: { suspense?: boolean }
) => {
  const timezone = useTimezone();

  const { rangeFrom, rangeTo } = useMemo(() => {
    const rangeFrom = CalendarDateService.date().tz(timezone).startOf('day').utc();
    const rangeTo = rangeFrom.add(daysAheadLimit + 1, 'days'); // add one because rangeTo is not inclusive

    return { rangeFrom: rangeFrom.format(), rangeTo: rangeTo.format() };
  }, [ daysAheadLimit, timezone ]);

  const { data, isLoading } = useQuery({
    queryKey: [ 'availability-slots', providerId, patientId, rangeFrom, rangeTo, timezone, excludeProviderIds ] as const,
    queryFn: ({ queryKey: [ _cacheKey, providerId, patientId, rangeFrom, rangeTo, _timezone, excludeProviderIds ] }) =>
      getAvailabilitySlotsForRange({
        datetime_from: rangeFrom,
        datetime_to: rangeTo,
        provider_id: providerId,
        patient_id: patientId,
        exclude_providers: excludeProviderIds,
      }),
    retry: false,
    enabled,
    ...opts
  });

  const slots = useMemo(() => {
    return data?.slots?.map(slot => CalendarDateService.date(slot).tz(timezone)) ?? [];
  }, [ data?.slots, timezone ]);

  return { slots, isLoading };
};